import { apiClient } from "@/api/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Type definitions
export type LoraStatus = "pending" | "training" | "completed" | "failed";

export interface LoraModel {
  lora_id: string;
  persona_id: string;
  name: string;
  version: string;
  trigger_word: string;
  training_images: string[];
  training_status: LoraStatus;
  model_url: string;
  created_at: string;
  last_updated: string;
  fal_training_id?: string;
  fal_model_id?: string;
  training_steps: number;
  learning_rate: number;
  thumbnail_url?: string;
  metadata?: Record<string, any>;
}

export interface LoraTrainingFormData {
  name: string;
  persona_id: string;
  trigger_word: string;
  training_steps?: number;
  learning_rate?: number;
  image_subject: "persona" | "custom";
  files: File[];
}

// API functions
export const getPersonaLoras = async (
  personaId: string,
): Promise<LoraModel[]> => {
  const response = await apiClient.get(`/api/loras?persona_id=${personaId}`);
  return response.data.loras || [];
};

export const deleteLora = async ({
  personaId,
  loraId,
}: {
  personaId: string;
  loraId: string;
}): Promise<any> => {
  const response = await apiClient.delete(
    `/api/loras/${loraId}?persona_id=${personaId}`,
  );
  return response.data;
};

export const uploadAndTrainLora = async (formData: FormData): Promise<any> => {
  const response = await apiClient.post(
    "/api/loras/upload-and-train",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// React Query hooks
export const usePersonaLorasQuery = (personaId: string | undefined) => {
  return useQuery({
    queryKey: ["loras", personaId],
    queryFn: () => getPersonaLoras(personaId || ""),
    enabled: !!personaId,
  });
};

export const useDeleteLoraMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteLora,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["loras", variables.personaId],
      });
    },
  });
};

export const useUploadAndTrainLoraMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: LoraTrainingFormData) => {
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("persona_id", data.persona_id);
      formData.append("trigger_word", data.trigger_word);
      formData.append("image_subject", data.image_subject);

      if (data.training_steps) {
        formData.append("training_steps", data.training_steps.toString());
      }

      if (data.learning_rate) {
        formData.append("learning_rate", data.learning_rate.toString());
      }

      // Append all files
      for (const file of data.files) {
        formData.append("files", file);
      }

      return uploadAndTrainLora(formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["loras", variables.persona_id],
      });
    },
  });
};
